import { z } from 'zod';
import {
  telemetryDisclosureSchema,
  horizonBandSchema,
  wearTrajectorySchema,
  confidenceTierSchema,
  telemetryClientCategoryScoreSchema,
} from './telemetry-score.js';
import { carbonUncertaintySchema } from './carbon.js';
import { usePhaseCustomerDetailResponseSchema } from './use-phase.js';
import { provenanceEventClientSchema } from './provenance.js';
import { manifestPassportClientSchema } from './asset-manifest.js';

/**
 * A status transition on the passport's lifecycle timeline — sourced from
 * `asset_lifecycle_event` (db/schema/498_asset_lifecycle_advance.sql,
 * widened by 678_asset_lifecycle_event_location_team.sql), NOT
 * `asset_provenance_event`. The two logs are deliberately separate: provenance
 * is a hash-chained custody/identity chain (who holds or has rights to the
 * asset), while this is a status-transition audit trail (what stage the asset
 * is in). Only `event_type = 'lifecycle_state'` rows ever reach this schema —
 * `location_change` / `team_change` rows are internal, org-only audit entries
 * (see 678's migration header) and must never be surfaced on a passport any
 * viewer (owner or buyer) can see.
 */
export const statusTransitionEventClientSchema = z.object({
  id: z.string().uuid(),
  occurredAt: z.string(),
  fromState: z.string().nullable(),
  toState: z.string().nullable(),
});
export type StatusTransitionEventClient = z.infer<typeof statusTransitionEventClientSchema>;

// ---------------------------------------------------------------------------
// Active Verification synthesis (SIL-585, spec §9/§13 Wave 4) — the SAME
// synthesized shape rendered on the passport §-section AND the listing
// (both consume this schema; see @siliconx/shared/verification/presentation
// for the pure synthesis function producing it, and PassportBody.tsx for the
// shared renderer both portals use). Optional-with-default so passports
// generated before this field existed still parse for hash re-derivation
// (same discipline as `environmental` below).
// ---------------------------------------------------------------------------
/**
 * SIL-613 — compute-correctness + VRAM pattern-test presentation shape.
 * Optional-with-default, same discipline as the rest of this schema's
 * evolution (see module header) — a passport hashed/generated before this
 * field existed still parses; `performed: false` is the correct "no
 * evidence" default, never a fabricated pass.
 */
const passportVerificationChecksSchema = z
  .object({
    compute: z.object({ performed: z.boolean(), passed: z.boolean() }),
    vram: z.object({ performed: z.boolean(), passed: z.boolean(), bytesTested: z.number().int().nonnegative() }),
  })
  .default({ compute: { performed: false, passed: false }, vram: { performed: false, passed: false, bytesTested: 0 } });

/**
 * SIL-688 — honest multi-GPU reporting. Optional-with-default (null) so a
 * passport rendered before this field existed still parses for hash
 * re-derivation, same discipline as `checks` above. `measuredIndex: null`
 * means WebGPU's chosen adapter could not be matched to one of `devices` —
 * the renderer (VerificationOutcome.tsx's `multiGpuCaveat`) must say "which
 * one could not be determined", never assume device 0.
 */
const passportVerificationHardwareSchema = z
  .object({
    gpuCount: z.number().int().nonnegative(),
    measuredIndex: z.number().int().nonnegative().nullable(),
    devices: z.array(
      z.object({
        index: z.number().int().nonnegative(),
        vendor: z.string(),
        name: z.string().optional(),
        serial: z.string().nullable(),
      }),
    ),
  })
  .nullable()
  .optional()
  .default(null);

export const passportVerificationSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }),
  z.object({
    kind: z.literal('tier12_pass'),
    tier: z.union([z.literal(1), z.literal(2)]),
    verdict: z.enum(['pass', 'pass_with_observations']),
    completedAt: z.string(),
    stale: z.boolean(),
    expiresAt: z.string(),
    passedOnRetry: z.boolean(),
    checks: passportVerificationChecksSchema,
    hardware: passportVerificationHardwareSchema,
  }),
  z.object({
    kind: z.literal('tier12_failed'),
    tier: z.union([z.literal(1), z.literal(2)]),
    classification: z.string(),
    completedAt: z.string(),
    checks: passportVerificationChecksSchema,
    hardware: passportVerificationHardwareSchema,
  }),
  z.object({
    kind: z.literal('tier12_flagged'),
    tier: z.union([z.literal(1), z.literal(2)]),
    classification: z.string(),
    completedAt: z.string(),
    checks: passportVerificationChecksSchema,
    hardware: passportVerificationHardwareSchema,
  }),
  z.object({
    kind: z.literal('tier3_self_reported'),
    passed: z.boolean(),
    verdict: z.string(),
    completedAt: z.string(),
    checks: passportVerificationChecksSchema,
    hardware: passportVerificationHardwareSchema,
  }),
]);
export type PassportVerification = z.infer<typeof passportVerificationSchema>;

/**
 * Digital passport client DTO (RUF-554).
 *
 * Design: docs/prds/digital-passport-health-confidence.md, passport page
 * structure §1-5. ONE client-safe projection assembled from data the API
 * already serves elsewhere (telemetry_score_snapshot, telemetry_daily_agg,
 * fmv_pricing_snapshot) — same ops/client schema split discipline as
 * telemetry-score.ts: NO features, NO modelVersion, NO fmv low/high/
 * confidence-breakdown (median-only, mirrors client/snapshots.ts).
 *
 * Route: GET /api/client/assets/:id/passport
 * (apps/api/src/routes/client/telemetry-score.ts).
 */

// ---------------------------------------------------------------------------
// §1 Identity & Coverage
// ---------------------------------------------------------------------------

/** One damage-budget metric — lifetime value vs the v5 model's own thresholds (design D7). */
export const damageBudgetMetricClientSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.number(),
  goodThreshold: z.number(),
  criticalThreshold: z.number(),
});
export type DamageBudgetMetricClient = z.infer<typeof damageBudgetMetricClientSchema>;

export const coverageGapClientSchema = z.object({
  start: z.string(),
  end: z.string(),
  days: z.number(),
});
export type CoverageGapClient = z.infer<typeof coverageGapClientSchema>;

/**
 * SIL-819 — the "Fingerprint": a Passport's public identifier, published in
 * place of the (owner-gated) serial number. A discriminated union, NOT a
 * single "unavailable" flag — `not_established`, `disputed`, and
 * `suppressed` are three distinct, never-collapsed states, and only
 * `established` ever carries a renderable value:
 *
 *   - `established` — a serial exists and no conflict is on file. `value`
 *     is the rendered `<prefix>-<token>` form (formatFingerprint(), token = asset.fingerprint_token); `tier` is the
 *     asset's current Active Verification tier (1|2|3, see
 *     @siliconx/shared/verification/presentation) and MUST always render
 *     beside `value` — never collapsed, truncated, or hover-only. Tier 3
 *     means the identity is backed only by a seller-reported, spoofable,
 *     in-band serial; hiding that provenance would launder the tier.
 *   - `not_established` — no serial on file (asset.serial_number is null).
 *     The asset still lists; it just has nothing to publish as a
 *     Fingerprint yet.
 *   - `disputed` — the in-band serial (asset.serial_number) and the OOB/
 *     BMC-attested serial (asset.observed_serial) disagree. One of the two
 *     sources is lying and which one is unknown, so a contested identity is
 *     never published.
 *   - `suppressed` — SIL-818 flagged this asset as a conflict
 *     (asset.suppressed_at) via the serial_collision review queue. Same
 *     "never publish a contested identity" rule as `disputed`, distinct
 *     reason.
 */
export const passportFingerprintSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('established'),
    value: z.string(),
    tier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  }),
  z.object({ status: z.literal('not_established') }),
  z.object({ status: z.literal('disputed') }),
  z.object({ status: z.literal('suppressed') }),
]);
export type PassportFingerprint = z.infer<typeof passportFingerprintSchema>;

export const passportIdentitySchema = z.object({
  /** Owner (seller) only — never surfaced to a buyer viewer. Mirrors client/listings.ts's isOwner masking. */
  serialNumber: z.string().nullable(),
  /**
   * The serial the owner typed once after a TrueRead probe missed it
   * (asset.user_provided_serial, migration 929). UNVERIFIED - it is never the
   * Fingerprint's input, and every surface must label it "user provided,
   * unverified" and render it masked, visibly apart from `serialNumber`.
   * Owner-gated exactly like `serialNumber`. Optional-with-default null so
   * render_payload rows generated before passport-document-v7 still parse for
   * hash re-derivation (a missing field means "no such claim was made").
   */
  userProvidedSerial: z.string().nullable().optional().default(null),
  /** SIL-819 — published to EVERY viewer, owner or not; the Fingerprint replaces the serial for non-owners, it never exposes it. */
  fingerprint: passportFingerprintSchema,
  skuId: z.string().uuid().nullable(),
  skuModelName: z.string().nullable(),
  manufacturerName: z.string().nullable(),
});
export type PassportIdentity = z.infer<typeof passportIdentitySchema>;

export const passportCoverageSchema = z.object({
  confidenceTier: confidenceTierSchema.nullable(),
  firstSeen: z.string().nullable(),
  lastSeen: z.string().nullable(),
  observedDays: z.number(),
  gaps: z.array(coverageGapClientSchema),
});
export type PassportCoverage = z.infer<typeof passportCoverageSchema>;

// ---------------------------------------------------------------------------
// §2 Health & Operational Fitness
// ---------------------------------------------------------------------------

export const passportHealthSchema = z.object({
  overallScore: z.coerce.number().min(0).max(100),
  overallCoverage: z.coerce.number().min(0).max(1),
  scoredAt: z.string(),
  horizonBand: horizonBandSchema.nullable(),
  wearTrajectory: wearTrajectorySchema.nullable(),
  categories: z.array(telemetryClientCategoryScoreSchema),
});
export type PassportHealth = z.infer<typeof passportHealthSchema>;

// ---------------------------------------------------------------------------
// Environmental — embodied carbon (SIL-190)
// ---------------------------------------------------------------------------

/**
 * Embodied-carbon disclaimer, embedded VERBATIM in every generated passport
 * payload (reproducibility: the payload snapshot must be self-contained,
 * same discipline as the FMV/telemetry blocks above).
 *
 * SIL-345: previously claimed the figures "exclude use-phase emissions" —
 * true of this passport section (use-phase is out of scope for the
 * passport in v1, see resources/plans/use-phase-carbon.md), but worded as
 * if SiliconX doesn't measure use-phase emissions at all, which became
 * false once the ESG CSV export started reporting them. Reworded to scope
 * the claim to THIS document without asserting anything about SiliconX's
 * broader carbon reporting.
 *
 * SIL-397: appended the "realistic production configuration, not a
 * manufacturer's minimum test configuration" disclosure. SIL-395 derives
 * installed RAM from each SKU/asset's max-supported capacity rather than
 * assuming a flat low baseline, which raises published figures by multiples
 * versus a typical vendor PCF (measured at a minimum test configuration,
 * e.g. 64 GB installed in an 8 TB-capable chassis) — expected and correct,
 * but silent about it reads as an error to a customer comparing the two.
 * This exact framing previously existed ONLY in the backend
 * `defaulted_fields.ram` provenance string (derive-input.ts), which is never
 * sent to any customer surface — this is the first customer-visible copy
 * carrying it. Ported, not re-authored, so the two never drift.
 */
export const PASSPORT_ENVIRONMENTAL_DISCLAIMER_TEXT =
  "Estimated embodied carbon (manufacturing, transport, end-of-life), computed via Boavizta's " +
  'open-source BoaviztAPI methodology. Estimates use available product specifications; missing ' +
  'fields use industry archetype defaults and are flagged. Figures are not independently audited. ' +
  "Embodied and operational emissions are reported separately and must never be added together: " +
  'embodied carbon is a one-time total for the hardware, while the operational figure is an annual ' +
  'rate that depends on where the asset is currently run. These figures model a realistic ' +
  'production configuration (e.g. RAM ' +
  "installed at a typical fraction of the hardware's maximum supported capacity), not a " +
  "manufacturer's minimum test configuration — a large multiple of a published manufacturer PCF is " +
  'expected and correct, not an error.';

/**
 * Placeholder copy when the asset has no matched SKU, or its SKU has no
 * computed carbon snapshot yet. Locked decision: the environmental section
 * ALWAYS renders — never silently omitted.
 */
export const PASSPORT_ENVIRONMENTAL_PENDING_TEXT =
  'Environmental data pending hardware classification';

/**
 * Regulatory framing for the environmental section. Names the frameworks the
 * `reportsCopy.ts` disclosure register already names (GHG Protocol, CSRD/ESRS
 * E1, CDP) and carries that register's discipline verbatim in substance: these
 * are figures the customer files themselves, the organizational-boundary
 * determination stays theirs, and SiliconX does not file on anyone's behalf.
 *
 * Deliberately ONE line rather than the register's three rows — a per-asset
 * document that duplicates the register will drift from it. Anything more
 * specific than "supports reporting under" would claim a compliance posture we
 * are not audited against.
 */
export const PASSPORT_ENVIRONMENTAL_REGULATORY_LINE =
  'Prepared to support reporting under the GHG Protocol Corporate Standard and Scope 2 Guidance, ' +
  'CSRD / ESRS E1, and CDP. These figures are an extract you file yourself: classifying them into ' +
  'your organizational boundary and consolidation approach remains your determination, and ' +
  'SiliconX does not file on your behalf.';

/**
 * Aggregation label for lot-level embodied-carbon totals (per-member values
 * and their min/max bounds summed independently — no correlation assumed).
 */
export const PASSPORT_LOT_ENVIRONMENTAL_AGGREGATION_LABEL =
  'sum of per-asset estimates (independent bounds summed)';

/**
 * The passport's environmental section — the full embodied-carbon snapshot
 * bundle (value, bounds, completeness, methodology version, method) captured
 * verbatim at generation time from v_sku_carbon_footprint_latest.
 * `carbonFootprint` is null when the asset has no SKU or its SKU has no
 * snapshot — renderers show PASSPORT_ENVIRONMENTAL_PENDING_TEXT.
 *
 * `scope2` is the operational (use-phase) figure — the SAME
 * `UsePhaseCustomerDetailResponse` the asset-detail card renders, embedded
 * whole rather than picked apart. That is load-bearing, not lazy: the
 * response type carries `scopeLabel` together with the companion statements
 * (`noMarketBasedStatement`, `boundsCaveat`, `screeningEstimateLine`) that
 * dto/use-phase.ts requires to accompany it, so there is no shape in which a
 * renderer receives a Scope-2-labeled number without them. Its `figure` is a
 * discriminated union — a `not_computed` status carries the reason, which is
 * how "no data" reaches the page. Never null, never omitted: an absent line
 * reads as zero emissions.
 *
 * NOTE — the figure is an ANNUAL RATE at the asset's current site and PUE, not
 * a lifetime total, and it is frozen at generation time in an issued artifact.
 * It must never be summed with `carbonFootprint` (a one-time embodied total);
 * see PASSPORT_ENVIRONMENTAL_DISCLAIMER_TEXT, which states this to the reader.
 *
 * `scope2: null` and `regulatoryLine: null` mean "this passport predates Scope 2
 * reporting" — they exist ONLY so render_payload rows written before
 * passport-document-v4 still parse for hash re-derivation, same discipline as
 * the `environmental` and `verification` defaults on the parent schema. The
 * assembler always populates both; a renderer seeing null must omit the block
 * rather than show a placeholder, because that document genuinely made no
 * Scope 2 claim. That is a different statement from a `not_computed` figure,
 * which means we looked and had no data.
 */
export const passportEnvironmentalSchema = z.object({
  carbonFootprint: carbonUncertaintySchema.extend({ computedAt: z.string() }).nullable(),
  scope2: usePhaseCustomerDetailResponseSchema.nullable().optional().default(null),
  disclaimer: z.string(),
  regulatoryLine: z
    .literal(PASSPORT_ENVIRONMENTAL_REGULATORY_LINE)
    .nullable()
    .optional()
    .default(null),
});
export type PassportEnvironmental = z.infer<typeof passportEnvironmentalSchema>;

// ---------------------------------------------------------------------------
// §5 Disclaimer & Verification
// ---------------------------------------------------------------------------

export const PASSPORT_DISCLAIMER_TEXT =
  'This passport is a projection of historical telemetry as reported; history ' +
  'before onboarding is unverified; it is not a warranty of future ' +
  'performance. A DCGM diagnostic is recommended on receipt.';

// ---------------------------------------------------------------------------
// SIL-1919 - Manufacturer entitlement record (OEM warranty data as reported)
// ---------------------------------------------------------------------------

/**
 * Disclosure printed under the warranty section. Counsel to confirm. Snapshot
 * tested - changing a word is a deliberate edit. Not a SiliconX warranty, not a
 * representation by SiliconX or the seller.
 */
export const PASSPORT_WARRANTY_DISCLOSURE_TEXT =
  "This is the manufacturer's entitlement record as reported at the time shown. " +
  'It is not a SiliconX warranty, and SiliconX does not provide or guarantee any warranty. ' +
  'This section is not a representation by SiliconX or the seller. ' +
  'Manufacturer warranty and support entitlements may not transfer to a new owner; ' +
  'confirm eligibility with the manufacturer. ' +
  "Dates reflect the manufacturer's records, which may have changed since.";

/**
 * Dates only. No serial, tag, URL, summary or status string appears anywhere in
 * this shape (no "in warranty" flag is stored or derived). `projection:'buyer'`
 * is the reduced buyer view (SIL-1950): month-precision latest end, service
 * levels and units with a record only.
 */
export const passportWarrantySchema = z.object({
  oem: z.enum(['dell']),
  /** Label source key (e.g. 'dell-techdirect'). Unknown keys render raw, never throw. */
  sourceAdapter: z.string().max(60),
  projection: z.enum(['owner', 'buyer']),
  scope: z.enum(['asset', 'stock_line']),
  /** Asset: that fetch. Stock line: the earliest fetch among units with a record. */
  fetchedAt: z.string(),
  quantity: z.number().int().positive(),
  unitsWithRecord: z.number().int().nonnegative(),
  /** Owner, asset scope only. */
  shipDate: z.string().nullable(),
  /** Owner only. */
  earliestEndDate: z.string().nullable(),
  /** Owner: YYYY-MM-DD. Buyer: YYYY-MM. */
  latestEndDate: z.string().nullable(),
  serviceLevels: z.array(z.string().max(120)).max(5),
  /** Owner, asset scope only; empty otherwise. */
  entitlements: z
    .array(
      z.object({
        startDate: z.string().nullable(),
        endDate: z.string().nullable(),
        serviceLevel: z.string().max(120).nullable(),
      }),
    )
    .max(6),
});
export type PassportWarranty = z.infer<typeof passportWarrantySchema>;

// ---------------------------------------------------------------------------
// Full passport response
// ---------------------------------------------------------------------------

export const telemetryPassportClientResponseSchema = z.object({
  identity: passportIdentitySchema,
  coverage: passportCoverageSchema,
  health: passportHealthSchema.nullable(),
  disclosures: z.array(telemetryDisclosureSchema),
  damageBudget: z.array(damageBudgetMetricClientSchema),
  fmv: z
    .object({
      medianUsd: z.number(),
      pricedAt: z.string(),
      telemetryMultiplier: z.number(),
      telemetryConfigVersion: z.string(),
      /**
       * SIL-1828 (R2): present (true) ONLY when the median derives from a
       * virtual baseline. Optional with no default and omitted when false, so
       * older render_payload rows re-derive their stored hash unchanged.
       */
      isDirectionalBaseline: z.literal(true).optional(),
    })
    .nullable(),
  /**
   * SIL-190 — always present on newly generated passports (renderers show a
   * pending placeholder when carbonFootprint is null; the section is never
   * omitted). Optional-with-default so render_payload rows generated before
   * passport-document-v3 still parse for hash re-derivation.
   */
  environmental: passportEnvironmentalSchema
    .optional()
    .default({ carbonFootprint: null, disclaimer: PASSPORT_ENVIRONMENTAL_DISCLAIMER_TEXT }),
  /** SIL-585 — synthesized Active Verification outcome (never a raw run
   * dump). Optional-with-default so pre-existing render_payload rows still
   * parse for hash re-derivation. */
  verification: passportVerificationSchema.optional().default({ kind: 'none' }),
  /**
   * SIL-821 — the asset's hash-chained provenance chain (asset_provenance_
   * event, 805), in seq order. For a merged (tombstoned) asset this is
   * always the SURVIVING asset's chain — see assemble.ts's
   * resolveAssetIdentityRow and 805's migration header for why. Optional-
   * with-default so render_payload rows generated before this field existed
   * still parse for hash re-derivation, same discipline as `environmental`
   * and `verification` above.
   */
  provenance: z.array(provenanceEventClientSchema).optional().default([]),
  /**
   * Status-transition events (active/idle/decommissioned/etc.) from
   * asset_lifecycle_event's event_type='lifecycle_state' rows — a SEPARATE
   * log from `provenance` above (see statusTransitionEventClientSchema's
   * doc comment for why the two are never merged into one vocabulary).
   * Optional-with-default so render_payload rows generated before this
   * field existed still parse for hash re-derivation, same discipline as
   * `provenance` and `environmental` above.
   */
  statusTransitions: z.array(statusTransitionEventClientSchema).optional().default([]),
  /**
   * SIL-825 — the current component manifest and its dated, tier-qualified
   * change history (asset_manifest_version/asset_manifest_component, 811).
   * Optional-with-default so render_payload rows generated before this
   * field existed still parse for hash re-derivation, same discipline as
   * `provenance` above. Never a verdict, warning, or risk score anywhere in
   * this shape — see assemble.ts and 811's migration header.
   */
  manifest: manifestPassportClientSchema.optional().default({ showManifest: false, current: null, history: [] }),
  /**
   * SIL-1919 - manufacturer entitlement record, from fetched OEM data. Absent
   * on every payload issued before passport-document-v9; `.optional().default(null)`
   * (same discipline as `userProvidedSerial`, `scope2`) so those parse to null and
   * render byte-identically to v8. Null also means "no record" or "not for this viewer".
   */
  warranty: passportWarrantySchema.nullable().optional().default(null),
  disclaimer: z.string(),
});
export type TelemetryPassportClientResponse = z.infer<typeof telemetryPassportClientResponseSchema>;
